/**
 * "Request an artistic edit" (beta) in the library's quick look: a shiny card that opens a short
 * form. The finished edit arrives in Exports with an email.
 * One open request per account; this video's request shows as "requested" instead of the form.
 */
import React, { useEffect, useState } from "react";
import IridescentCherry from "./IridescentCherry";
import { ART } from "./studioText";
import { getJson, MyStudioRequest, postJson, STUDIO_REQUESTS_URL } from "./studioApi";
import { getUserTranscriptVideosUrl } from "../constants";
import { IN_FLIGHT_STATUSES } from "../pages/myLibrary/constants/taskStatuses";
import { ROUTES } from "../../constants";

const MAX_VIDEOS = 10;

interface Props {
  /** The video this card sits on (the quick look); it starts ticked. None = the library's page. */
  transcriptId?: number;
  /** Open with the form showing (the ready email's "Request one" link: ?art=1, or the page). */
  startOpen?: boolean;
}

interface Pickable { id: number; title: string; thumbnail?: string | null; task_status?: string }

const ArtisticRequest: React.FC<Props> = ({ transcriptId, startOpen = false }) => {
  const [mine, setMine] = useState<MyStudioRequest | null>(null);
  const [library, setLibrary] = useState<Pickable[]>([]);
  const [picked, setPicked] = useState<number[]>(transcriptId ? [transcriptId] : []);
  const [showAll, setShowAll] = useState(!transcriptId);
  const [open, setOpen] = useState(startOpen);
  const [about, setAbout] = useState("");
  const [wants, setWants] = useState("");
  const [audience, setAudience] = useState("");
  const [shape, setShape] = useState("either");
  const [feedback, setFeedback] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    getJson(STUDIO_REQUESTS_URL).then(({ ok, data }) => {
      if (cancelled || !ok) return;
      const rows: MyStudioRequest[] = data.requests || [];
      // On a video: that video's request. On the page: the open one, if any.
      const found = transcriptId
        ? rows.find((r) => (r.transcript_ids || [r.transcript_id]).includes(transcriptId))
        : rows.find((r) => r.status === "new" || r.status === "working");
      setMine(found || null);
    }).catch(() => { /* the card still offers the form */ });
    return () => { cancelled = true; };
  }, [transcriptId]);

  // Their finished videos, to tick (only once the form is open).
  useEffect(() => {
    if (!open || library.length) return undefined;
    let cancelled = false;
    getJson(getUserTranscriptVideosUrl).then(({ ok, data }) => {
      if (cancelled || !ok) return;
      const list: Pickable[] = (Array.isArray(data) ? data : data.videos || [])
        .filter((v: Pickable) => !(v.task_status && (IN_FLIGHT_STATUSES.has(v.task_status as never) || v.task_status === "failed")));
      setLibrary(list);
    }).catch(() => { /* the current video stays ticked */ });
    return () => { cancelled = true; };
  }, [open, library.length]);

  const toggle = (id: number) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= MAX_VIDEOS ? p : [...p, id]));
  const listed = showAll ? library : library.filter((v) => picked.includes(v.id));

  const send = async () => {
    if (!picked.length) { setMessage(ART.needVideo); return; }
    if (!wants.trim()) { setMessage(ART.needWants); return; }
    setBusy(true);
    setMessage("");
    try {
      const { ok, data } = await postJson(STUDIO_REQUESTS_URL, {
        transcript_ids: picked, about, wants, audience, shape, will_give_feedback: feedback,
      });
      if (ok) { setMine(data); setOpen(false); setMessage(ART.sent); } else setMessage(data.message || ART.failed);
    } catch {
      setMessage(ART.failed);
    } finally {
      setBusy(false);
    }
  };

  const done = mine && mine.status !== "declined";

  return (
    <div className="artcard">
      <div className="art-hd">
        <IridescentCherry size={34} />
        <div className="art-tx">
          <b>{done ? (mine?.status === "delivered" ? ART.delivered : ART.requested) : ART.title} <em>{ART.beta}</em></b>
          <span>{done ? (mine?.status === "delivered" ? ART.deliveredNote : ART.requestedNote) : ART.pitch}</span>
        </div>
        {!done && (
          <button type="button" className={`pill ${open ? "line" : "art-go"}`} onClick={() => setOpen((o) => !o)}>
            {open ? ART.close : ART.open}
          </button>
        )}
      </div>
      {message && !open && <div className="art-msg">{message}</div>}
      {transcriptId && (
        <div className="art-more">
          {done && mine?.status !== "delivered"
            ? <a href={ROUTES.MY_LIBRARY_ARTISTIC}>{ART.addMoreLink}</a>
            : !done && <>{ART.severalVideos} <a href={ROUTES.MY_LIBRARY_ARTISTIC}>{ART.severalVideosLink}</a></>}
        </div>
      )}
      {open && !done && (
        <form className="art-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <div className="art-videos">
            <div className="art-row"><span>{ART.videos(picked.length)}</span>
              {transcriptId && !showAll && <button type="button" className="chip" onClick={() => setShowAll(true)}>{ART.addMore}</button>}
            </div>
            <div className="art-pick">
              {listed.map((v) => (
                <button type="button" key={v.id} className={`art-v${picked.includes(v.id) ? " on" : ""}`} onClick={() => toggle(v.id)} title={v.title}>
                  <span className="art-th">{v.thumbnail ? <img src={v.thumbnail} alt="" /> : null}<i>{picked.includes(v.id) ? picked.indexOf(v.id) + 1 : "+"}</i></span>
                  <span className="art-vt">{v.title}</span>
                </button>
              ))}
            </div>
          </div>
          <label>{ART.wants}
            <textarea rows={3} value={wants} placeholder={ART.wantsPlaceholder} onChange={(e) => setWants(e.target.value)} />
          </label>
          <label>{ART.about}
            <input value={about} placeholder={ART.aboutPlaceholder} onChange={(e) => setAbout(e.target.value)} />
          </label>
          <div className="art-row">
            <span>{ART.audience}</span>
            {ART.audiences.map((a) => (
              <button type="button" key={a.key} className={`chip${audience === a.key ? " on" : ""}`} onClick={() => setAudience(audience === a.key ? "" : a.key)}>{a.label}</button>
            ))}
          </div>
          <div className="art-row">
            <span>{ART.shape}</span>
            {ART.shapes.map((s) => (
              <button type="button" key={s.key} className={`chip${shape === s.key ? " on" : ""}`} onClick={() => setShape(s.key)}>{s.label}</button>
            ))}
          </div>
          <label className="art-check">
            <input type="checkbox" checked={feedback} onChange={(e) => setFeedback(e.target.checked)} /> {ART.feedback}
          </label>
          <div className="art-row end">
            <span className="art-fine">{ART.fine}</span>
            <button type="submit" className="pill art-go" disabled={busy}>{busy ? ART.sending : ART.send}</button>
          </div>
          {message && <div className="art-msg">{message}</div>}
        </form>
      )}
    </div>
  );
};

export default ArtisticRequest;
